import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PermissionGroupV2, PermissionV2 } from '../../../types/role';

/** What RoleForm edits — a platform role, or an organization's role. */
export interface RoleFormInitial {
  name: string;
  permissions: PermissionV2[];
  isBuiltIn?: boolean;
}

interface RoleFormProps {
  initial?: RoleFormInitial | null;
  permissionGroups: PermissionGroupV2[];
  onSubmit: (payload: { name: string; permissionIds: string[] }) => Promise<void> | void;
  onCancel: () => void;
  isSubmitting?: boolean;
  errorMessage?: string;
}

const ROLE_NAME_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;

export const RoleForm = ({
  initial,
  permissionGroups,
  onSubmit,
  onCancel,
  isSubmitting = false,
  errorMessage,
}: RoleFormProps) => {
  const { t } = useTranslation();
  const isBuiltIn = !!initial?.isBuiltIn;

  const [name, setName] = useState(initial?.name ?? '');
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initial?.permissions.map((p) => p.id) ?? []),
  );

  useEffect(() => {
    setName(initial?.name ?? '');
    setSelected(new Set(initial?.permissions.map((p) => p.id) ?? []));
  }, [initial]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGroup = (group: PermissionGroupV2, on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const p of group.permissions) {
        if (on) next.add(p.id);
        else next.delete(p.id);
      }
      return next;
    });
  };

  const groupAllSelected = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const g of permissionGroups) {
      map.set(g.group, g.permissions.every((p) => selected.has(p.id)));
    }
    return map;
  }, [permissionGroups, selected]);

  const nameError = useMemo(() => {
    if (!name.trim()) return t('dashboard.roles.errors.nameRequired');
    if (!ROLE_NAME_PATTERN.test(name))
      return t('dashboard.roles.errors.nameFormat');
    return null;
  }, [name, t]);

  const isDirty = useMemo(() => {
    const initialIds = new Set(initial?.permissions.map((p) => p.id) ?? []);
    if (initialIds.size !== selected.size) return true;
    for (const id of selected) if (!initialIds.has(id)) return true;
    return name !== (initial?.name ?? '');
  }, [initial, name, selected]);

  const canSubmit =
    !isSubmitting &&
    isDirty &&
    selected.size > 0 &&
    (isBuiltIn || !nameError);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const payload: { name?: string; permissionIds: string[] } = {
      permissionIds: Array.from(selected),
    };
    if (!isBuiltIn) payload.name = name.trim();
    await onSubmit(payload as { name: string; permissionIds: string[] });
  };

  return (
    <form onSubmit={handleSubmit} className="role-form">
      <div className="role-form-field">
        <label htmlFor="role-name" className="role-form-label">
          {t('dashboard.roles.form.name')}
        </label>
        <input
          id="role-name"
          type="text"
          maxLength={40}
          className="filter-search-input"
          value={name}
          disabled={isBuiltIn}
          onChange={(e) => setName(e.target.value.toLowerCase())}
          placeholder={t('dashboard.roles.form.namePlaceholder')}
          aria-invalid={!!nameError}
        />
        {isBuiltIn ? (
          <p className="role-form-hint">
            {t('dashboard.roles.form.builtInHint')}
          </p>
        ) : nameError ? (
          <p className="role-form-error">{nameError}</p>
        ) : (
          <p className="role-form-hint">
            {t('dashboard.roles.form.nameHint')}
          </p>
        )}
      </div>

      <fieldset className="role-form-field" style={{ border: 0, padding: 0 }}>
        <legend className="role-form-label">
          {t('dashboard.roles.form.permissions')}
        </legend>
        <p className="role-form-hint">
          {t('dashboard.roles.form.permissionsHint', {
            count: selected.size,
          })}
        </p>
        <div className="permission-groups">
          {permissionGroups.map((group) => {
            const allOn = groupAllSelected.get(group.group) ?? false;
            return (
              <section key={group.group} className="permission-group">
                <header className="permission-group-header">
                  <h4 className="permission-group-title">
                    {t(`dashboard.roles.groups.${group.group}`, {
                      defaultValue: group.group,
                    })}
                  </h4>
                  <button
                    type="button"
                    className="permission-group-toggle"
                    onClick={() => toggleGroup(group, !allOn)}
                  >
                    {allOn
                      ? t('dashboard.roles.form.deselectAll')
                      : t('dashboard.roles.form.selectAll')}
                  </button>
                </header>
                <ul className="permission-list">
                  {group.permissions.map((p) => (
                    <li key={p.id} className="permission-item">
                      <label className="permission-checkbox">
                        <input
                          type="checkbox"
                          checked={selected.has(p.id)}
                          onChange={() => toggle(p.id)}
                        />
                        <span>{p.title}</span>
                        <code className="permission-name">{p.name}</code>
                      </label>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </fieldset>

      {errorMessage && <p className="role-form-error">{errorMessage}</p>}

      <div className="role-form-actions">
        <button
          type="button"
          className="secondary-action-btn"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t('dashboard.roles.form.cancel')}
        </button>
        <button
          type="submit"
          className="primary-action-btn"
          disabled={!canSubmit}
        >
          {isSubmitting
            ? t('dashboard.roles.form.saving')
            : t('dashboard.roles.form.save')}
        </button>
      </div>
    </form>
  );
};

export default RoleForm;
